import { isEqual } from 'lodash';
import { validate as isUuid } from 'uuid';
import { ParameterError } from '../types/errors';
import { ChartType } from '../types/savedCharts';
import assertUnreachable from './assertUnreachable';
export const DOCUMENT_CHART_TAG = 'document-chart';
/** Links to a saved chart; the Document shows its latest version. */
export const SAVED_CHART_TAG = 'saved-chart';
/** Links to a saved SQL chart; the Document shows its latest version. */
export const SAVED_SQL_CHART_TAG = 'saved-sql-chart';
const SAVED_CHART_TAG_KINDS = {
    [SAVED_CHART_TAG]: 'chart',
    [SAVED_SQL_CHART_TAG]: 'sqlChart',
};
export const getSavedChartTagName = (kind) => kind === 'chart' ? SAVED_CHART_TAG : SAVED_SQL_CHART_TAG;
/**
 * Lightdash block tag names contain a hyphen, which no HTML element does, so
 * tags added by a newer release are recognised here and kept rather than read
 * as markdown.
 */
export const isDocumentBlockTagName = (name) => name.includes('-');
const TAG_LINE_RE = /^<([a-z][a-z-]*)((?:\s+[a-z][a-z-]*="[^"]*")*)\s*\/?>(?:<\/\1>)?$/;
const ATTRIBUTE_RE = /([a-z][a-z-]*)="([^"]*)"/g;
const FENCE_RE = /^(`{3,}|~{3,})/;
const DOCUMENT_CHART_ID_RE = /^c([1-9]\d*)$/;
const decodeAttribute = (value) => value
    .replaceAll('&quot;', '"')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&amp;', '&');
const encodeAttribute = (value) => value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
/** Drop surrounding blank lines, keeping the first line's indentation. */
const trimBlankLines = (lines) => {
    const start = lines.findIndex((line) => line.trim() !== '');
    if (start === -1)
        return '';
    const end = lines.findLastIndex((line) => line.trim() !== '');
    return lines
        .slice(start, end + 1)
        .join('\n')
        .trimEnd();
};
export const parseDocumentBlocks = (markdown, tagNames) => {
    const isTagName = typeof tagNames === 'function'
        ? tagNames
        : (name) => tagNames.includes(name);
    const blocks = [];
    let lines = [];
    let fence = null;
    const flush = () => {
        const text = trimBlankLines(lines);
        if (text)
            blocks.push({ type: 'markdown', markdown: text });
        lines = [];
    };
    markdown.split(/\r?\n/).forEach((line) => {
        const fenceMatch = FENCE_RE.exec(line.trim());
        if (fence !== null) {
            if (fenceMatch && fenceMatch[1].startsWith(fence))
                fence = null;
            lines.push(line);
            return;
        }
        if (fenceMatch) {
            [, fence] = fenceMatch;
            lines.push(line);
            return;
        }
        const tagMatch = TAG_LINE_RE.exec(line.trimEnd());
        if (tagMatch && isTagName(tagMatch[1])) {
            flush();
            blocks.push({
                type: 'tag',
                line: line.trimEnd(),
                tag: {
                    name: tagMatch[1],
                    attributes: Object.fromEntries([...tagMatch[2].matchAll(ATTRIBUTE_RE)].map(([, key, value]) => [key, decodeAttribute(value)])),
                },
            });
            return;
        }
        lines.push(line);
    });
    flush();
    return blocks;
};
export const formatDocumentTag = ({ name, attributes }) => `<${name}${Object.entries(attributes)
    .map(([key, value]) => ` ${key}="${encodeAttribute(value)}"`)
    .join('')}>`;
export const joinDocumentBlocks = (blocks) => blocks
    .map((block) => block.type === 'markdown'
    ? block.markdown
    : formatDocumentTag(block.tag))
    .join('\n\n');
export const getDocumentChartTag = (id) => formatDocumentTag({ name: DOCUMENT_CHART_TAG, attributes: { id } });
/** The number of a stored chart id (`c3` → 3), or undefined for a temporary key. */
export const getDocumentChartNumber = (id) => {
    const match = DOCUMENT_CHART_ID_RE.exec(id);
    return match ? Number(match[1]) : undefined;
};
/**
 * The Document in reading order. Chart tags must reference an entry in
 * `charts` or `unsupportedCharts`, once each.
 */
export const getDocumentChartBlocks = (content) => {
    const seen = new Set();
    const unsupportedCharts = content.unsupportedCharts ?? {};
    return parseDocumentBlocks(content.markdown, isDocumentBlockTagName).map((block) => {
        if (block.type === 'markdown')
            return block;
        const savedChartKind = Object.hasOwn(SAVED_CHART_TAG_KINDS, block.tag.name)
            ? SAVED_CHART_TAG_KINDS[block.tag.name]
            : undefined;
        if (savedChartKind) {
            const { uuid, slug } = block.tag.attributes;
            if (!uuid === !slug) {
                throw new ParameterError(`Every <${block.tag.name}> tag needs either a uuid or a slug`);
            }
            if (uuid && !isUuid(uuid)) {
                throw new ParameterError(`<${block.tag.name} uuid="${uuid}"> isn't a valid uuid`);
            }
            return {
                type: 'savedChart',
                kind: savedChartKind,
                attributes: block.tag.attributes,
            };
        }
        if (block.tag.name !== DOCUMENT_CHART_TAG) {
            return {
                type: 'unsupportedTag',
                line: block.line ?? formatDocumentTag(block.tag),
            };
        }
        const { id } = block.tag.attributes;
        if (!id) {
            throw new ParameterError(`Every <${DOCUMENT_CHART_TAG}> tag needs an id`);
        }
        if (seen.has(id)) {
            throw new ParameterError(`Chart "${id}" is placed more than once`);
        }
        seen.add(id);
        if (Object.hasOwn(content.charts, id)) {
            return { type: 'chart', id, chart: content.charts[id] };
        }
        if (Object.hasOwn(unsupportedCharts, id)) {
            return {
                type: 'unsupportedChart',
                id,
                raw: unsupportedCharts[id],
            };
        }
        throw new ParameterError(`Chart "${id}" is placed in the markdown but missing from charts`);
    });
};
const toMarkdownBlock = (block) => {
    switch (block.type) {
        case 'markdown':
            return block;
        case 'unsupportedTag':
            return { type: 'markdown', markdown: block.line };
        case 'chart':
        case 'unsupportedChart':
            return {
                type: 'tag',
                tag: { name: DOCUMENT_CHART_TAG, attributes: { id: block.id } },
            };
        case 'savedChart':
            return {
                type: 'tag',
                tag: {
                    name: getSavedChartTagName(block.kind),
                    attributes: block.attributes,
                },
            };
        default:
            return assertUnreachable(block, 'Unknown Document block');
    }
};
/**
 * Canonical content: chart tags carry only their id, unplaced charts are
 * dropped, and `unsupportedCharts` is omitted when empty.
 */
export const fromDocumentChartBlocks = (blocks) => {
    const unsupportedCharts = Object.fromEntries(blocks.flatMap((block) => block.type === 'unsupportedChart' ? [[block.id, block.raw]] : []));
    return {
        markdown: joinDocumentBlocks(blocks.map(toMarkdownBlock)),
        charts: Object.fromEntries(blocks.flatMap((block) => block.type === 'chart' ? [[block.id, block.chart]] : [])),
        ...(Object.keys(unsupportedCharts).length > 0
            ? { unsupportedCharts }
            : {}),
    };
};
export const mapDocumentCharts = (content, map) => ({
    ...content,
    charts: Object.fromEntries(Object.entries(content.charts).map(([id, chart]) => [
        id,
        map(chart, id),
    ])),
});
/**
 * Replace temporary chart keys with stored ids. Keys already shaped like
 * `c<n>` are kept; the others get the next free numbers in reading order, so
 * an id is never handed out twice for one Document.
 */
export const assignDocumentChartIds = (content, nextChartNumber) => {
    const blocks = getDocumentChartBlocks(content);
    let next = Math.max(nextChartNumber, ...blocks.map((block) => block.type === 'chart' || block.type === 'unsupportedChart'
        ? (getDocumentChartNumber(block.id) ?? 0) + 1
        : 1));
    const assigned = blocks.map((block) => {
        if (block.type !== 'chart' ||
            getDocumentChartNumber(block.id) !== undefined) {
            return block;
        }
        const id = `c${next}`;
        next += 1;
        return { ...block, id };
    });
    return {
        content: fromDocumentChartBlocks(assigned),
        nextChartNumber: next,
    };
};
export const getDocumentChartList = (content) => getDocumentChartBlocks(content).flatMap((block) => block.type === 'chart' ? [{ id: block.id, chart: block.chart }] : []);
const getChartTypeLabel = (content) => {
    if (content.source === 'sql') {
        return content.chart.chartKind;
    }
    const { chart } = content;
    if (chart.chartConfig.type !== ChartType.CARTESIAN) {
        return chart.chartConfig.type;
    }
    return chart.chartConfig.config?.eChartsConfig.series?.[0]?.type ?? 'bar';
};
/**
 * The Document with each chart tag expanded into a short description (id,
 * title, type, explore), for readers that should not receive full charts.
 */
export const getDocumentSummaryMarkdown = (content) => joinDocumentBlocks(getDocumentChartBlocks(content).map((block) => {
    if (block.type === 'markdown')
        return block;
    if (block.type === 'unsupportedTag' ||
        block.type === 'savedChart') {
        return toMarkdownBlock(block);
    }
    if (block.type === 'unsupportedChart') {
        return {
            type: 'tag',
            tag: {
                name: DOCUMENT_CHART_TAG,
                attributes: { id: block.id, unsupported: 'true' },
            },
        };
    }
    const placed = block.chart;
    return {
        type: 'tag',
        tag: {
            name: DOCUMENT_CHART_TAG,
            attributes: {
                id: block.id,
                title: placed.chart.name,
                type: getChartTypeLabel(placed),
                ...(placed.source === 'sql'
                    ? { source: placed.source }
                    : { explore: placed.chart.tableName }),
                ...(placed.source === 'merge'
                    ? { source: placed.source }
                    : {}),
            },
        },
    };
}));
/** Tags an AI agent writes to place charts from its conversation. */
export const ARTIFACT_CHART_TAG = 'artifact-chart';
export const QUERY_RESULT_TAG = 'query-result';
export const DOCUMENT_CONVERSATION_TAGS = [
    ARTIFACT_CHART_TAG,
    QUERY_RESULT_TAG,
];
/**
 * Give a temporary chart key the stored id of an identical, otherwise unplaced
 * chart in the previous version, so re-sending an unchanged chart under a
 * temporary key (e.g. re-uploading a file) does not copy it.
 */
export const matchDocumentChartKeys = (content, previous) => {
    const blocks = getDocumentChartBlocks(content);
    const used = new Set(blocks.flatMap((block) => block.type === 'chart' &&
        getDocumentChartNumber(block.id) !== undefined
        ? [block.id]
        : []));
    return fromDocumentChartBlocks(blocks.map((block) => {
        if (block.type !== 'chart' ||
            getDocumentChartNumber(block.id) !== undefined) {
            return block;
        }
        const match = Object.entries(previous.charts).find(([id, chart]) => !used.has(id) && isEqual(chart, block.chart));
        if (!match)
            return block;
        used.add(match[0]);
        return { ...block, id: match[0] };
    }));
};
/** The saved charts a Document links to, in reading order. */
export const getDocumentSavedChartLinks = (content) => getDocumentChartBlocks(content).flatMap((block) => block.type === 'savedChart' ? [block] : []);
/** Rewrite each link's attributes, e.g. a slug to the uuid it resolves to. */
export const mapDocumentSavedChartLinks = (content, map) => fromDocumentChartBlocks(getDocumentChartBlocks(content).map((block) => block.type === 'savedChart'
    ? { ...block, attributes: map(block) }
    : block));
//# sourceMappingURL=documentMarkdown.js.map